/*
 * Project: BookingEngine
 * Module: utils/Generic.js
 * vim:ft=javascript.jsx:
 * @flow
 */

/** ************************************************************************** */
/* Utilizades genéricas
/**************************************************************************** */
import React from 'react';
import ThumborUrlBuilder from 'thumbor-url-builder';
import moment from 'moment';
import type { RoomType } from '../FlowTypes';
import { CALL_CENTER_ROLE } from '../constants';

const DATE_MASK = 'YYYY-MM-DD';

const getLangBoardDescription = (board: Object, lang: string): string => {
  // Since there are two multilang fields for descriptions, we look for the first field which
  // is either more specific to the hotel, and / or that is for the specified language
  const sortedFieldNames = [
    `hotel_board_description_${lang}`,
    `description_${lang}`,
    'hotel_board_description',
    'description',
  ];
  const boardDescField = sortedFieldNames.find((fieldName) => !!board[fieldName]);
  return board[boardDescField] || '';
};

/** ************************************************************************** */
/* A partir de un objeto, devuelve el valor del atributo en función del
 * idioma indicado. Esto va muy bien para los nombres de habitación, pensión,
 * etc...
/**************************************************************************** */
export const getLangValue = (object: Object, lang: string, attribute: string): string => {
  if (attribute === 'boardDescription') {
    return getLangBoardDescription(object, lang);
  }
  const value = object[`${attribute}_${lang}`];
  return value || object[attribute];
};

/** ************************************************************************** */
/* Devuelve el precio mínimo de la habitación para cualquier tarifa y régimen,
 * para la divisa indicada
/**************************************************************************** */
export const getRoomMinAvgPrice = (room: RoomType, currencyCode: string, nights: number) => {
  let priceFrom = null;
  room.rates.forEach((rate) => {
    rate.boards.forEach((board) => {
      const currencyAmount = board.amount[currencyCode].amount;
      if (currencyAmount && (priceFrom === null || priceFrom > currencyAmount)) {
        priceFrom = currencyAmount;
      }
    });
  });
  let price = priceFrom || room.price_from;
  if (!Number.isNaN(nights)) {
    price /= nights;
  }
  return price.toFixed(2);
};

/** ************************************************************************** */
/* Dada una url de una foto, la pasa por thumbor
/**************************************************************************** */
export const getPhotoThumb = ({
  url, width, height, props, config, extraClass,
}) => {
  const hei = height || 0;
  const thumbor = new ThumborUrlBuilder(process.env.THUMBOR_KEY, process.env.THUMBOR_URL);
  const smartCrop = !config || config.smartCrop === undefined ? true : config.smartCrop;
  const url_thumbor = thumbor.setImagePath(url).resize(parseInt(width, 10), hei).smartCrop(smartCrop).buildUrl();

  // Combina las clases. En caso de que manden una clase extra, se lo ponemos
  const className = extraClass
    ? `sandos-img ${extraClass}`
    : 'sandos-img';
  return <img className={className} src={url_thumbor} {...props} />;
};

export const getPhotoThumbUrl = (url: string, width?: number, height?: number) => {
  const hei = height || 0;
  const thumbor = new ThumborUrlBuilder(process.env.THUMBOR_KEY, process.env.THUMBOR_URL);
  return thumbor.setImagePath(url).resize(parseInt(width, 10), hei).buildUrl();
};

/** ************************************************************************** */
/* Construye el texto para distancias
/**************************************************************************** */
export const getDistanceText = (distance: number) => (
  distance < 1000 ? `${distance.toFixed(0)} m` : `${(distance / 1000).toFixed(1)} km`
);

// Method used for the autogenerate-search url. It detects if the dates are from now or not.
export const getCheckInCheckOutDaysFrom = (daysFrom, daysTo, returnFormatted) => {
  let checkIn;
  let checkOut;

  if (daysFrom.substr(0, 1) === 'd') {
    checkIn = moment().add(parseInt(daysFrom.replace('d', ''), 10), 'd');
    checkOut = moment().add(parseInt(daysTo.replace('d', ''), 10), 'd');
  } else {
    checkIn = moment(daysFrom, DATE_MASK, true);
    checkOut = moment(daysTo, DATE_MASK, true);
  }

  if (returnFormatted) {
    checkIn = checkIn.format(DATE_MASK);
    checkOut = checkOut.format(DATE_MASK);
  }
  return { checkIn, checkOut };
};

// Método usado para comprobar un email suando regexp.
export const isValidEmail = (email) => {
  const regExpPattern = /(.+)@(.+){2,}\.(.+){2,}/;
  return regExpPattern.test(email);
};

// Method which evaluates if the user in the store belongs to a Call Center user
export const isCallCenterUser = (sessionState) => {
  const { user } = sessionState;
  return user && user.role === CALL_CENTER_ROLE;
};

export const toCustomCase = (str) =>
  str
    .replace(/[^a-zA-Z0-9]+/g, ' ') // Replace non-alphanumeric characters with spaces
    .trim() // Remove leading and trailing whitespace
    .split(' ') // Split the string into words
    .map((word) => {
      const capitalizedWord = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      return capitalizedWord; // Capitalize the first letter of each word
    })
    .join(' '); // Join the words together
